import { useContext } from 'react'
import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'

import { faCheck, faClock, faHome } from '@fortawesome/pro-light-svg-icons'

import { getQueryParamsFromHash, parsePermittedQueryParams } from '@domain-group/javascript-utils'
import {
  Box, Button, Columns, LoadingModule, PageHeader, Tools,
} from '@domain-group/suit-theme'

import type { EntitiesState } from '@redux/entities'

import PageContext, { DefaultContext } from '@contexts/pageContext'

import { UserModel } from '@models/types'

import { statusKeys } from '@functions/status'
import { getTaskTypeSetting, subTaskTypes, taskTypes } from '@functions/taskListItem'

import useSelector from '@hooks/useSelector'
import useTaskListItem from '@hooks/useTaskListItem'
import useTaskListItems from '@hooks/useTaskListItems'
import useToggleListItem from '@hooks/useToggleListItem'

import PageFilterModule from '@components/PageFilterModule'

import defaultRequestOptions from '@sections/Admin/packs/Tasks/defaultRequestOptions'

import type { PageContextPayload } from '../../index'

import TaskBlankState from '../TaskBlankState'

import SubTasksTable from '../SubTasksTable'
import TasksTable from '../TasksTable'

const getActiveTabBarKey = (isCompleted: boolean, isOverdue: boolean, isSubTasks: boolean) => {
  if (isSubTasks) return 'subTasks'
  if (isCompleted) return 'completed'
  if (isOverdue) return 'overdue'
  return 'templates'
}

const buildPageFilterFields = (entities: EntitiesState) => {
  const { statuses } = entities

  const style = { marginBottom: 16 }
  const boxProps = { marginBottom: 'large' }

  return [
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'string',
      label: 'Filter by Title or ID',
    },
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'template_page_id',
      label: 'Template page id',
    },
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'brands',
        label: 'Brand',
      },
      key: 'brand_id',
    },
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'taskLists',
        label: 'Project',
      },
      key: 'task_list_id',
    },
    {
      callbacks: {
        // @ts-expect-error TS7031
        buildSearchRequestOptions: ({ task_list_id }) => ({ task_list_id }),
        // @ts-expect-error TS7031
        shouldRender: ({ task_list_id }) => !!task_list_id,
      },
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'taskListSections',
        label: 'Project Group',
      },
      key: 'task_list_section_id',
    },
    {
      component: 'DynamicInput',
      componentProps: {
        defaultSelectLabel: 'All',
        inputComponent: 'select',
        style,
      },
      key: 'task_type',
      label: 'Task Type',
      values: [...taskTypes, { key: '' }, ...subTaskTypes].map(taskType => ({
        key: taskType,
        // @ts-expect-error TS2345
        label: getTaskTypeSetting(taskType, 'label') ?? '---',
      })),
    },
    {
      component: 'DynamicInput',
      componentProps: {
        defaultSelectLabel: 'All',
        inputComponent: 'select',
        style,
      },
      key: 'priority',
      label: 'Priority',
      values: [
        { key: 'high', label: 'High' },
        { key: 'medium', label: 'Medium' },
        { key: 'default', label: 'Default' },
      ],
    },
    {
      component: 'DynamicInput',
      componentProps: {
        defaultSelectLabel: 'All',
        inputComponent: 'select',
        style,
      },
      key: 'status_id',
      label: 'Status',
      values: Object.values(statuses)
        .filter(s => statusKeys.includes(s.key))
        .map((status) => {
          const { id, title } = status
          return { key: id, label: title }
        }),
    },
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'providers',
        label: 'Provider',
      },
      key: 'provider_id',
    },
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        callbacks: {
          getSearchEntities: (results: UserModel[]) => results.map((user) => {
            const { id, full_name } = user
            return { ...user, full_name: `#${id} - ${full_name}` }
          }),
        },
        entityKey: 'users',
        entityTitleKey: 'full_name',
        label: 'Assignee',
        showEntityId: true,
      },
      key: 'assignee_id',
    },
    {
      components: [
        {
          component: 'DynamicInput',
          componentProps: {
            inputComponent: 'input',
            inputType: 'date',
            style,
          },
          key: 'start_due_date',
          label: 'Start Date',
        },
        {
          component: 'DynamicInput',
          componentProps: {
            inputComponent: 'input',
            inputType: 'date',
            style,
          },
          key: 'end_due_date',
          label: 'End Date',
        },
      ],
      label: 'Due Date',
      key: 'due_date',
    },
    {
      components: [
        {
          component: 'DynamicInput',
          componentProps: {
            inputComponent: 'input',
            inputType: 'date',
            style,
          },
          key: 'start_created_date',
          label: 'Created Start Date',
        },
        {
          component: 'DynamicInput',
          componentProps: {
            inputComponent: 'input',
            inputType: 'date',
            style,
          },
          key: 'end_created_date',
          label: 'Created End Date',
        },
      ],
      label: 'Created Date',
      key: 'created_date',
    },
    {
      components: [
        {
          component: 'DynamicInput',
          componentProps: {
            inputComponent: 'input',
            inputType: 'date',
            style,
          },
          key: 'start_completed_date',
          label: 'Completed Start Date',
        },
        {
          component: 'DynamicInput',
          componentProps: {
            inputComponent: 'input',
            inputType: 'date',
            style,
          },
          key: 'end_completed_date',
          label: 'Completed End Date',
        },
      ],
      label: 'Completion Date',
      key: 'completed_date',
    },
  ]
}

interface PageContentProps {
  pageFilters: object,
}

const PageContent = (props: PageContentProps) => {
  const { pageFilters } = props

  // useLocation to refresh the window.location. If not used location won't reload if URL changes
  useLocation()

  const entities = useSelector(reduxState => reduxState.entities)

  const { completed, overdue, sub_tasks } = parsePermittedQueryParams(getQueryParamsFromHash(), [
    'completed',
    'overdue',
    'sub_tasks',
  ])
  const isCompleted = completed === 'true'
  const isSubTasks = sub_tasks === 'true'
  const isOverdue = overdue === 'true'

  // @ts-expect-error TS2345
  const pageContext = useContext<DefaultContext<PageContextPayload>>(PageContext)
  const {
    callbacks: {
      resetFilters, showBulkAssignUserTasksModal, showCreateOrEditTaskListItemModal, updateFilters,
    },
    defaultFilters,
  } = pageContext

  const taskListItemsPayload = useTaskListItems({
    filters: {
      ...pageFilters,
      completed: isCompleted,
      overdue: isOverdue,
      parent_task_id: isSubTasks ? undefined : 'none',
      sub_tasks: isSubTasks,
      template_project: true,
    },
    performHttpRequests: true,
    requestOptions: {
      ...defaultRequestOptions.image,
      ...defaultRequestOptions.user,
      include_related_entity_subject: true,
      include_task_list_item_assignee: true,
      include_task_list_item_brand_id: true,
      include_task_list_item_parent_template_ids: isSubTasks,
      include_task_list_item_parent_task: isSubTasks,
      include_task_list_item_related_entities: true,
      include_task_list_item_task_list: true,
      include_task_list_owner: true,
    },
  })

  const { hasFilteredTaskListItems, loading } = taskListItemsPayload

  const useToggleListItemPayload = useToggleListItem()
  const {
    callbacks: { setState },
    hasItems,
    items: selectedTaskListItemIds,
  } = useToggleListItemPayload

  const { callbacks: { manageTaskListItems } } = useTaskListItem()

  const Component = isSubTasks ? SubTasksTable : TasksTable

  return (
    <>
      <Helmet>
        <title>Tasks | Template Manager</title>
      </Helmet>

      <PageHeader
        actionContent={(
          <Button buttonStyle="primaryCreate" onClick={() => showCreateOrEditTaskListItemModal()} size="medium">
            Create
          </Button>
        )}
        activeTabBarItemKey={getActiveTabBarKey(isCompleted, isOverdue, isSubTasks)}
        boxProps={{ height: [112, 105], justifyContent: 'flex-start' }}
        tabBarItems={[
          {
            href: '#/tasks?sub_tasks=false',
            icon: faHome,
            key: 'templates',
            title: 'Templates',
          },
          {
            href: '#/tasks?sub_tasks=true',
            icon: faHome,
            key: 'subTasks',
            title: 'Tasks',
          },
          {
            href: '#/tasks?overdue=true&sub_tasks=false',
            icon: faClock,
            key: 'overdue',
            title: 'Overdue',
          },
          {
            href: '#/tasks?completed=true&sub_tasks=false',
            icon: faCheck,
            key: 'completed',
            title: 'Completed',
          },
        ]}
        title="Tasks"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <LoadingModule loading={loading} times={3} />

            {!loading && hasFilteredTaskListItems && (
              <Component
                entities={entities}
                taskListItemsPayload={taskListItemsPayload}
                useToggleListItemPayload={useToggleListItemPayload}
              />
            )}

            {!hasFilteredTaskListItems && !loading && <TaskBlankState action={showBulkAssignUserTasksModal} />}
          </Columns.Main>

          <Columns.Sidebar>
            <Tools>
              <Button
                buttonStyle="secondaryUtility"
                disabled={!hasItems}
                onClick={() => manageTaskListItems({
                  resetItems: () => setState({ items: [] }),
                  taskListItemIds: selectedTaskListItemIds,
                })}
                size="medium"
              >
                Manage Task Details
              </Button>
            </Tools>

            <PageFilterModule
              callbacks={{ resetFilters, updateFilters }}
              defaultFilters={defaultFilters}
              // @ts-expect-error TS2322
              filterFields={buildPageFilterFields(entities)}
              pageFilters={pageFilters}
            />
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

export default PageContent
